<template>
  <section class="login-page" :class="{ 'is-dark': isDark }">
    <div class="login-container">
      <div class="login-header">
        <h3 class="login-title">
          <span>Two-Factor Authentication</span>
        </h3>
      </div>

      <div class="login-card two-factor-card">
        <div class="two-factor-content">
          <div class="two-factor-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>

          <p class="two-factor-description">
            {{ $t('twoFactor.enterCode') || 'Enter the 6-digit code from your authenticator app' }}
          </p>

          <form @submit.prevent="handleVerify" class="two-factor-form">
            <div class="field">
              <div class="control">
                <input
                  ref="codeInput"
                  v-model="code"
                  class="input code-input"
                  :class="{ 'is-danger': error }"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="12"
                  :placeholder="$t('twoFactor.codePlaceholder') || 'Enter code'"
                  autocomplete="one-time-code"
                  :disabled="processing"
                  @input="clearError"
                />
              </div>
              <p v-if="error" class="help is-danger">{{ error }}</p>
            </div>

            <div class="field">
              <div class="control">
                <button
                  type="submit"
                  class="button is-primary is-fullwidth"
                  :class="{ 'is-loading': processing }"
                  :disabled="processing || !code"
                >
                  {{ $t('twoFactor.verify') || 'Verify' }}
                </button>
              </div>
            </div>
          </form>

          <div class="recovery-link">
            <a href="#" @click.prevent="toggleRecoveryMode">
              {{ useRecoveryCode 
                ? ($t('twoFactor.useAuthenticator') || 'Use authenticator code') 
                : ($t('twoFactor.useRecoveryCode') || 'Use recovery code') }}
            </a>
          </div>

          <div class="back-link">
            <a href="#" @click.prevent="handleCancel">
              {{ $t('twoFactor.backToLogin') || 'Back to login' }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import twoFactorApi from '../api/twoFactor'

export default {
  name: 'TwoFactorVerify',
  data() {
    return {
      code: '',
      error: '',
      processing: false,
      useRecoveryCode: false
    }
  },
  computed: {
    isDark() {
      return this.$store.state.ui.dark
    },
    pendingToken() {
      return this.$route.query.token || sessionStorage.getItem('pending_2fa_token')
    }
  },
  mounted() {
    if (!this.pendingToken) {
      this.$router.push('/login')
      return
    }
    this.$nextTick(() => {
      if (this.$refs.codeInput) {
        this.$refs.codeInput.focus()
      }
    })
  },
  methods: {
    clearError() {
      this.error = ''
    },
    toggleRecoveryMode() {
      this.useRecoveryCode = !this.useRecoveryCode
      this.code = ''
      this.error = ''
    },
    async handleVerify() {
      if (!this.code) {
        this.error = this.$t('twoFactor.codeRequired') || 'Code is required'
        return
      }

      this.processing = true
      this.error = ''

      try {
        const response = await twoFactorApi.validateLogin(this.$http, this.pendingToken, this.code)
        const data = response?.data?.data || response?.data || {}

        if (data.token) {
          sessionStorage.removeItem('pending_2fa_token')
          this.handleLoginSuccess(data)
        } else {
          this.error = this.$t('twoFactor.invalidCode') || 'Invalid verification code'
        }
      } catch (err) {
        const message = err.response?.data?.message || err.message
        this.error = message || (this.$t('twoFactor.verificationFailed') || 'Verification failed')
      } finally {
        this.processing = false
      }
    },
    handleLoginSuccess(data) {
      try {
        const tokenParts = data.token.split('.')
        if (tokenParts.length !== 3) return

        const jwtClaims = JSON.parse(atob(tokenParts[1]))
        const user = {
          id: jwtClaims.sub || jwtClaims.user_id,
          name: jwtClaims.name || jwtClaims.username,
          expired_at: parseInt(jwtClaims.exp),
          scopes: jwtClaims.scopes || []
        }

        this.$store.dispatch('READ_NOTIFICATIONS_FROM', 'authentication')
        this.$store.commit('authentication/AUTHENTICATION_SET_TOKEN', data.token)
        this.$store.commit('authentication/AUTHENTICATION_SET_USER', user)
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('authentication.success') || 'Login successful',
          duration: 3000
        })

        this.$router.push('/').catch(err => console.error('Navigation error:', err))
      } catch (e) {
        console.error('Failed to parse JWT token:', e)
      }
    },
    handleCancel() {
      sessionStorage.removeItem('pending_2fa_token')
      this.$router.push('/login')
    }
  }
}
</script>

<style lang="scss" scoped>
$text-light: #363636;
$text-dark: #e8ebf1;

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 2rem;

  &.is-dark {
    background: linear-gradient(135deg, #0d0d1a 0%, #0f1525 50%, #0a1a30 100%);
  }
}

.login-container {
  width: 100%;
  max-width: 420px;
}

.login-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.login-title {
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
}

.two-factor-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;

  .is-dark & {
    background: #1e2535;
  }
}

.two-factor-content {
  text-align: center;
}

.two-factor-icon {
  margin-bottom: 1.5rem;
  color: #3273dc;

  .is-dark & {
    color: #3a7afe;
  }
}

.two-factor-description {
  color: $text-light;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;

  .is-dark & {
    color: $text-dark;
  }
}

.two-factor-form {
  margin-bottom: 1rem;
}

.code-input {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  font-family: monospace;

  &::placeholder {
    font-size: 1rem;
    letter-spacing: normal;
  }
}

.field {
  margin-bottom: 1rem;
}

.input {
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background-color: #ffffff;
  color: $text-light;

  &:focus {
    outline: none;
    border-color: #3273dc;
    box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
  }

  &.is-danger {
    border-color: #f14668;
  }

  .is-dark & {
    background-color: #2b3040;
    border-color: #3a4156;
    color: $text-dark;
  }
}

.help.is-danger {
  color: #f14668;
  font-size: 0.75rem;
  margin-top: 0.25rem;

  .is-dark & {
    color: #ff6b9d;
  }
}

.button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &.is-primary {
    background-color: #3273dc;
    color: #ffffff;

    &:hover:not(:disabled) {
      background-color: #2366d1;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.is-loading {
      color: transparent;
      pointer-events: none;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 1rem;
        height: 1rem;
        margin: -0.5rem;
        border: 2px solid #ffffff;
        border-right-color: transparent;
        border-radius: 50%;
        animation: spin 0.5s linear infinite;
      }
    }
  }

  &.is-fullwidth {
    width: 100%;
  }
}

.recovery-link,
.back-link {
  margin-top: 1rem;

  a {
    color: #3273dc;
    font-size: 0.875rem;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    .is-dark & {
      color: #3a7afe;
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
